import React, { useCallback, useState } from 'react';
import { NotificationContext } from './NotificationContext';
import { GearNotification, GearNotificationProps } from '../../components/common/GearNotification';
import { NotificationCenterItem } from './NotificationCenterItem';

interface NotificationContextProviderProps {
  children: React.ReactNode;
}

export const NotificationContextProvider: React.FC<NotificationContextProviderProps> = ({ children }) => {
  const [toastNotificationProps, setToastNotificationProps] = useState<GearNotificationProps | null>(null);
  const [centerNotifications, setCenterNotifications] = useState<NotificationCenterItem[]>([]);

  const showToast = useCallback((props: Omit<GearNotificationProps, 'show'>): void => {
    setToastNotificationProps({ ...props, show: true });
  }, [setToastNotificationProps]);

  const addCenterNotification = (notification: NotificationCenterItem): void => {
    setCenterNotifications(prev => [notification, ...prev]);
  };

  const updateCenterNotification = (notificationId: string, updates: Partial<NotificationCenterItem>): void => {
    setCenterNotifications(prev =>
      prev.map(notification => {
        if (notification.id === notificationId) {
          return {
            ...notification,
            ...updates,
            progress:
              notification.progress && updates.progress
                ? { ...notification.progress, ...updates.progress }
                : updates.progress || notification.progress
          };
        }
        return notification;
      })
    );
  };

  const removeCenterNotification = (notificationId: string): void => {
    setCenterNotifications(prev => prev.filter(x => x.id !== notificationId));
  };

  const clearAllCenterNotifications = (): void => {
    setCenterNotifications([]);
  };

  const unreadCenterCount = React.useMemo(() => {
    return centerNotifications.filter(notification => !notification.read).length;
  }, [centerNotifications]);

  const markAllAsRead = (): void => {
    if (unreadCenterCount > 0) {
      setCenterNotifications(prev => prev.map(x => ({ ...x, read: true })));
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        showToast,
        centerNotifications,
        addCenterNotification,
        updateCenterNotification,
        removeCenterNotification,
        clearAllCenterNotifications,
        markAllAsRead,
        unreadCenterCount
      }}
    >
      {children}
      {toastNotificationProps && (
        <GearNotification {...toastNotificationProps} onClose={() => setToastNotificationProps(null)} />
      )}
    </NotificationContext.Provider>
  );
};
